// src/components/AffiliateView.js
import React, { useState } from 'react';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Avatar,
    Button,
    Box,
    List,
    ListItem,
    Divider,
    Chip,
    Paper,
    Link,
    useTheme,
    useMediaQuery,
    CircularProgress,
} from '@mui/material';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LanguageIcon from '@mui/icons-material/Language';
import DomainIcon from '@mui/icons-material/Domain';
import EditIcon from '@mui/icons-material/Edit';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import PaymentIcon from '@mui/icons-material/Payment';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import KeyIcon from '@mui/icons-material/Key';
import LockIcon from '@mui/icons-material/Lock';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptIcon from '@mui/icons-material/Receipt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StorefrontIcon from '@mui/icons-material/Storefront';

import AffiliateEdit from './AffiliateEdit';
import StebbyApiModal from './StebbyApiModal';
import MontonioApiModal from './MontonioApiModal';
import { uploadAffiliateLogo } from '../api/logoApi';
import { formatCurrency } from '../utils/currency';

const PAYMENT_COUNTRY_LABELS = {
    EE: "Estonia",
    LV: "Latvia",
    LT: "Lithuania",
    PL: "Poland",
    FI: "Finland",
};

export default function AffiliateView({ token, affiliate, trainers, onUpdateAffiliate, getAffiliate, onChangePassword }) {
    const [editing, setEditing] = useState(false);
    const [stebbyModalOpen, setStebbyModalOpen] = useState(false);
    const [montonioModalOpen, setMontonioModalOpen] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleEdit = () => setEditing(true);
    const handleCancel = () => setEditing(false);

    const handleSave = async (updatedAffiliate) => {
        await onUpdateAffiliate(updatedAffiliate);
        setEditing(false);
        getAffiliate()
    };

    // Function for uploading logo
    const handleLogoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        try {
            const result = await uploadAffiliateLogo(file, affiliate.id, token);
            // Update affiliate data with new logo
            onUpdateAffiliate(result.affiliate);
        } catch (error) {
            alert(error.message);
        }
    };

    // Early return if affiliate is not loaded yet
    if (!affiliate) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '400px', gap: 2 }}>
                <CircularProgress size={60} />
                <Typography variant="h6" color="text.secondary">
                    Loading affiliate information...
                </Typography>
            </Box>
        );
    }

    // Parse freeFirstTrainingList from JSON string if needed
    const getFreeTrainingList = () => {
        if (!affiliate.freeFirstTrainingList) return [];

        try {
            // If it's already an array, return it
            if (Array.isArray(affiliate.freeFirstTrainingList)) {
                return affiliate.freeFirstTrainingList;
            }

            // If it's a string, try to parse it
            if (typeof affiliate.freeFirstTrainingList === 'string') {
                return JSON.parse(affiliate.freeFirstTrainingList);
            }

            return [];
        } catch (error) {
            console.error('Error parsing freeFirstTrainingList:', error);
            return [];
        }
    };

    const freeTrainingList = getFreeTrainingList();

    // Function to get registration period display text
    const getRegistrationPeriodText = (period) => {
        if (!period) return '1 week (default)';
        return period === 1 ? '1 week' : `${period} weeks`;
    };

    const getPaymentCountryLabel = (country) => (
        PAYMENT_COUNTRY_LABELS[country] || PAYMENT_COUNTRY_LABELS.EE
    );

    const getEnabledMontonioPaymentMethods = () => {
        const methods = [];
        if (affiliate.isMontonioBankPaymentEnabled !== false) methods.push({ label: "Bank payment", color: "info" });
        if (affiliate.isMontonioCardPaymentEnabled === true) methods.push({ label: "Card payment", color: "success" });
        if (affiliate.paymentCountry === "FI" && (affiliate.paymentCurrency || "EUR") === "EUR" && affiliate.isMontonioCardPaymentEnabled === true) methods.push({ label: "MobilePay", color: "primary" });
        if (affiliate.paymentCurrency === "PLN" && affiliate.isMontonioBlikPaymentEnabled === true) methods.push({ label: "BLIK", color: "secondary" });
        return methods.length > 0 ? methods : [{ label: "Bank payment", color: "info" }];
    };

    const paymentCurrency = affiliate.paymentCurrency || "EUR";
    const formatMoney = (value) => formatCurrency(value, paymentCurrency);

    if (editing) {
        return (
            <AffiliateEdit
                affiliate={affiliate}
                trainers={trainers}
                onSave={handleSave}
                onCancel={handleCancel}
            />
        );
    }

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
                {/* Main info card with logo */}
                <Card sx={{ bgcolor: 'background.paper', boxShadow: 2, borderRadius: 2 }}>
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                        <Avatar
                            src={affiliate.logo || 'https://via.placeholder.com/120'}
                            sx={{
                                width: '100%',
                                height: 'auto',
                                maxWidth: '300px',
                                maxHeight: '300px',
                                margin: 'auto',
                                backgroundColor: 'transparent',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                            }}
                            variant="square"
                        />
                        <Typography variant="h5" sx={{ mt: 3, fontWeight: 600 }}>
                            {affiliate.name || 'No affiliate name'}
                        </Typography>
                        <Chip
                            label={affiliate.trainingType || 'No training type'}
                            color="primary"
                            size="small"
                            sx={{ mt: 1 }}
                        />

                        {/* Action buttons with responsive layout */}
                        <Box sx={{
                            mt: 3,
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 2,
                            flexDirection: { xs: 'column', sm: 'row' },
                            width: { xs: '100%', sm: 'auto' }
                        }}>
                            <Button
                                variant="contained"
                                startIcon={<EditIcon />}
                                onClick={handleEdit}
                                sx={{
                                    borderRadius: 2,
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                            >
                                Add/Edit
                            </Button>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<FileUploadIcon />}
                                sx={{
                                    borderRadius: 2,
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                            >
                                Upload Logo
                                <input type="file" accept="image/*" hidden onChange={handleLogoChange}/>
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<KeyIcon />}
                                onClick={() => setStebbyModalOpen(true)}
                                sx={{
                                    borderRadius: 2,
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                            >
                                Stebby API
                            </Button>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<PaymentIcon />}
                                onClick={() => setMontonioModalOpen(true)}
                                sx={{
                                    borderRadius: 2,
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                            >
                                Montonio API
                            </Button>
                            {onChangePassword && (
                                <Button
                                    variant="outlined"
                                    color="warning"
                                    startIcon={<LockIcon />}
                                    onClick={onChangePassword}
                                    sx={{
                                        borderRadius: 2,
                                        width: { xs: '100%', sm: 'auto' }
                                    }}
                                >
                                    Change Password
                                </Button>
                            )}
                        </Box>

                        {/* Website and subdomain information moved below the buttons */}
                        <Box sx={{ mt: 3, py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <LanguageIcon sx={{ color: "primary.main" }}/>
                                <Link href={affiliate.website} target="_blank" underline="hover">
                                    {affiliate.website || 'No website provided'}
                                </Link>
                            </Box>
                            { affiliate.subdomain && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <DomainIcon sx={{ color: "primary.main" }}/>
                                    <Link href={`https://${affiliate.subdomain}.irontrack.ee`} target="_blank" underline="hover">
                                        {affiliate.subdomain}.irontrack.ee
                                    </Link>
                                </Box>
                            )}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography variant="body2" color="text.secondary">Feedback link:</Typography>
                                <Link href={`https://${affiliate.feedback}`} target="_blank" underline="hover">
                                    {affiliate.feedback}
                                </Link>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {/* Contact information card - improved */}
                <Card sx={{ mt: 3, bgcolor: 'background.paper', boxShadow: 2, borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon fontSize="small" />
                            Contact Information
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <EmailIcon sx={{ color: "primary.main" }}/>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Email</Typography>
                                            <Typography variant="body1">
                                                {affiliate.email || 'No email provided'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <PhoneAndroidIcon sx={{ color: "primary.main" }}/>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Phone</Typography>
                                            <Typography variant="body1">
                                                {affiliate.phone || 'No phone provided'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>

                            <Grid item xs={12}>
                                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <LocationOnIcon sx={{ color: "primary.main" }}/>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Address</Typography>
                                            <Typography variant="body1">
                                                {affiliate.address || 'No address provided'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Business Information card */}
                <Card sx={{ mt: 3, bgcolor: 'background.paper', boxShadow: 2, borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessIcon fontSize="small" />
                            Business Information
                        </Typography>
                        <Grid item xs={12}>
                            <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                    <LocationOnIcon sx={{ color: "primary.main" }}/>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Legal Name</Typography>
                                        <Typography variant="body1">
                                            {affiliate.legalName || 'No legal name provided'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <BusinessIcon sx={{ color: "primary.main" }}/>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Registry Code</Typography>
                                            <Typography variant="body1">
                                                {affiliate.registryCode || 'Not provided'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <ReceiptIcon sx={{ color: "primary.main" }}/>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">VAT Number</Typography>
                                            <Typography variant="body1">
                                                {affiliate.VATNumber || 'Not provided'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>

                            <Grid item xs={12}>
                                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <ReceiptIcon sx={{ color: "primary.main" }}/>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" color="text.secondary">VAT Rate</Typography>
                                            <Typography variant="body1">
                                                {affiliate.VATRate !== null && affiliate.VATRate !== undefined
                                                    ? `${affiliate.VATRate}%`
                                                    : 'Not set'}
                                            </Typography>
                                        </Box>
                                        {affiliate.VATRate !== null && affiliate.VATRate !== undefined && (
                                            <Chip
                                                label={`${affiliate.VATRate}%`}
                                                color="info"
                                                variant="outlined"
                                                sx={{ fontWeight: 'bold' }}
                                            />
                                        )}
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Banking Information card */}
                <Card sx={{ mt: 3, bgcolor: 'background.paper', boxShadow: 2, borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccountBalanceIcon fontSize="small" />
                            Banking Information
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <AccountBalanceIcon sx={{ color: "primary.main" }}/>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">IBAN</Typography>
                                            <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                                {affiliate.iban || 'Not provided'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <AccountBalanceIcon sx={{ color: "primary.main" }}/>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Bank Name</Typography>
                                            <Typography variant="body1">
                                                {affiliate.bankName || 'Not provided'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>

                            <Grid item xs={12}>
                                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <PaymentIcon sx={{ color: "primary.main" }}/>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" color="text.secondary">Payment Country</Typography>
                                            <Typography variant="body1" fontWeight={500}>
                                                {getPaymentCountryLabel(affiliate.paymentCountry)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Used as the preferred country for Montonio bank payments
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={affiliate.paymentCountry || "EE"}
                                            color="info"
                                            variant="outlined"
                                            sx={{ fontWeight: 'bold' }}
                                        />
                                    </Box>
                                </Paper>
                            </Grid>

                            <Grid item xs={12}>
                                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <PaymentIcon sx={{ color: "primary.main" }}/>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" color="text.secondary">Payment Currency</Typography>
                                            <Typography variant="body1" fontWeight={500}>
                                                {paymentCurrency}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Used as the currency for Montonio payments
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={paymentCurrency}
                                            color="success"
                                            variant="outlined"
                                            sx={{ fontWeight: 'bold' }}
                                        />
                                    </Box>
                                </Paper>
                            </Grid>

                            <Grid item xs={12}>
                                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <PaymentIcon sx={{ color: "primary.main" }}/>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" color="text.secondary">Montonio Payment Methods</Typography>
                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                                                {getEnabledMontonioPaymentMethods().map((method) => (
                                                    <Chip
                                                        key={method.label}
                                                        label={method.label}
                                                        color={method.color}
                                                        variant="outlined"
                                                        sx={{ fontWeight: 'bold' }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Registration Settings card */}
                <Card sx={{ mt: 3, bgcolor: 'background.paper', boxShadow: 2, borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ScheduleIcon fontSize="small" />
                            Registration Settings
                        </Typography>

                        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <ScheduleIcon sx={{ color: "primary.main" }}/>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" color="text.secondary">Registration Period</Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {getRegistrationPeriodText(affiliate.registrationPeriod)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Users can register for classes this far in advance
                                    </Typography>
                                </Box>
                                <Chip
                                    label={getRegistrationPeriodText(affiliate.registrationPeriod)}
                                    color="info"
                                    variant="outlined"
                                    sx={{ fontWeight: 'bold' }}
                                />
                            </Box>
                        </Paper>


                        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <ScheduleIcon sx={{ color: "primary.main" }}/>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" color="text.secondary">Cancel Before Hours</Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {affiliate.cancelBeforeHours !== null && affiliate.cancelBeforeHours !== undefined
                                            ? `${affiliate.cancelBeforeHours} ${affiliate.cancelBeforeHours === 1 ? 'hour' : 'hours'}`
                                            : '2 hours (default)'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Users can cancel class registration this many hours before class starts
                                    </Typography>
                                </Box>
                                <Chip
                                    label={affiliate.cancelBeforeHours !== null && affiliate.cancelBeforeHours !== undefined
                                        ? `${affiliate.cancelBeforeHours}h`
                                        : '2h'}
                                    color="warning"
                                    variant="outlined"
                                    sx={{ fontWeight: 'bold' }}
                                />
                            </Box>
                        </Paper>
                        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <EmailIcon sx={{ color: "primary.main" }}/>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" color="text.secondary">Plan Purchase Notifications</Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {affiliate.sendPlanBuyEmail !== false ? 'Enabled' : 'Disabled'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Receive email notifications when users purchase plans
                                    </Typography>
                                </Box>
                                <Chip
                                    label={affiliate.sendPlanBuyEmail !== false ? 'ON' : 'OFF'}
                                    color={affiliate.sendPlanBuyEmail !== false ? 'success' : 'default'}
                                    variant="outlined"
                                    sx={{ fontWeight: 'bold' }}
                                />
                            </Box>
                        </Paper>

                        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <StorefrontIcon sx={{ color: "primary.main" }}/>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" color="text.secondary">Shop visibility</Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {affiliate.isShopActive ? 'Enabled' : 'Disabled'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Controls if regular users can open Shop from Register Training page
                                    </Typography>
                                </Box>
                                <Chip
                                    label={affiliate.isShopActive ? 'ON' : 'OFF'}
                                    color={affiliate.isShopActive ? 'success' : 'default'}
                                    variant="outlined"
                                    sx={{ fontWeight: 'bold' }}
                                />
                            </Box>
                        </Paper>

                        {/* ✅ Lisa see uus sektsioon Class List Visibility jaoks: */}
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <VisibilityIcon sx={{ color: "primary.main" }}/>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" color="text.secondary">Class Participant List</Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {affiliate.isAllClassListVisible
                                            ? 'Active plan - Members with an active package'
                                            : affiliate.isClassListVisible
                                                ? 'Registered Only - Only registered members can see'
                                                : 'Hidden - No one can see'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Controls who can view class participant lists
                                    </Typography>
                                </Box>
                                <Chip
                                    label={
                                        affiliate.isAllClassListVisible
                                            ? 'ACTIVE PLAN'
                                            : affiliate.isClassListVisible
                                                ? 'REGISTERED'
                                                : 'HIDDEN'
                                    }
                                    color={
                                        affiliate.isAllClassListVisible
                                            ? 'success'
                                            : affiliate.isClassListVisible
                                                ? 'info'
                                                : 'warning'
                                    }
                                    variant="outlined"
                                    sx={{ fontWeight: 'bold' }}
                                />
                            </Box>
                        </Paper>
                        <Paper elevation={0} sx={{ p: 2, mt: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <PersonIcon sx={{ color: "primary.main" }}/>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" color="text.secondary">Trainer Names in Class Schedule</Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {affiliate.showTrainerNamesInSchedule !== false
                                            ? 'Visible to regular users'
                                            : 'Hidden from regular users'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Club-specific visibility setting
                                    </Typography>
                                </Box>
                                <Chip
                                    label={affiliate.showTrainerNamesInSchedule !== false ? 'VISIBLE' : 'HIDDEN'}
                                    color={affiliate.showTrainerNamesInSchedule !== false ? 'success' : 'warning'}
                                    variant="outlined"
                                    sx={{ fontWeight: 'bold' }}
                                />
                            </Box>
                        </Paper>
                        <Paper elevation={0} sx={{ p: 2, mt: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <VisibilityIcon sx={{ color: "primary.main" }}/>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" color="text.secondary">Workout Info Visibility</Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {affiliate.workoutInfoVisibilityMode === "registered"
                                            ? "Registered Only - Users must be registered"
                                            : affiliate.workoutInfoVisibilityMode === "plan"
                                                ? "Plan Required - Compatible class-type plan required"
                                                : "Always - Visible to users"}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={
                                        affiliate.workoutInfoVisibilityMode === "registered"
                                            ? "REGISTERED"
                                            : affiliate.workoutInfoVisibilityMode === "plan"
                                                ? "PLAN"
                                                : "ALWAYS"
                                    }
                                    color={
                                        affiliate.workoutInfoVisibilityMode === "registered"
                                            ? "info"
                                            : affiliate.workoutInfoVisibilityMode === "plan"
                                                ? "warning"
                                                : "success"
                                    }
                                    variant="outlined"
                                    sx={{ fontWeight: 'bold' }}
                                />
                            </Box>
                        </Paper>
                    </CardContent>
                </Card>

                {/* Free First Training List card */}
                <Card sx={{ mt: 3, bgcolor: 'background.paper', boxShadow: 2, borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FitnessCenterIcon fontSize="small" />
                            Free trial classes for new member registration
                        </Typography>

                        {freeTrainingList && freeTrainingList.length > 0 ? (
                            <Grid container spacing={2}>
                                {freeTrainingList.map((training, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                bgcolor: 'background.default',
                                                borderRadius: 2,
                                                transition: 'all 0.3s',
                                                '&:hover': {
                                                    boxShadow: 1,
                                                    transform: 'translateY(-2px)'
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                <Avatar
                                                    sx={{
                                                        bgcolor: 'success.main',
                                                        width: 40,
                                                        height: 40
                                                    }}
                                                >
                                                    <FitnessCenterIcon />
                                                </Avatar>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body1" fontWeight={500}>
                                                        {training}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <Paper elevation={0} sx={{ p: 3, bgcolor: 'background.default', borderRadius: 2, textAlign: 'center' }}>
                                <Typography color="text.secondary">No free first training programs available.</Typography>
                            </Paper>
                        )}
                    </CardContent>
                </Card>

                {/* Trainers card - improved */}
                <Card sx={{ mt: 3, bgcolor: 'background.paper', boxShadow: 2, borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon fontSize="small" />
                            Trainers
                        </Typography>

                        {trainers.length > 0 ? (
                            <Grid container spacing={2}>
                                {trainers.map((trainer) => (
                                    <Grid item xs={12} sm={6} md={4} key={trainer.trainerId}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                bgcolor: 'background.default',
                                                borderRadius: 2,
                                                transition: 'all 0.3s',
                                                '&:hover': {
                                                    boxShadow: 1,
                                                    transform: 'translateY(-2px)'
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                <Avatar
                                                    sx={{
                                                        bgcolor: 'primary.main',
                                                        width: 40,
                                                        height: 40
                                                    }}
                                                >
                                                    {trainer.fullName.charAt(0)}
                                                </Avatar>
                                                <Typography variant="body1" fontWeight={500}>
                                                    {trainer.fullName}
                                                </Typography>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <Paper elevation={0} sx={{ p: 3, bgcolor: 'background.default', borderRadius: 2, textAlign: 'center' }}>
                                <Typography color="text.secondary">No trainers assigned to this affiliate.</Typography>
                            </Paper>
                        )}
                    </CardContent>
                </Card>

                {/* Payment Holiday Settings card */}
                <Card sx={{ mt: 3, bgcolor: 'background.paper', boxShadow: 2, borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <PaymentIcon fontSize="small" />
                            Payment Holiday Settings
                        </Typography>

                        <Paper elevation={0} sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>Holiday Fee:</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Fee charged for payment holiday months
                                    </Typography>
                                </Box>
                                <Chip
                                    label={affiliate.paymentHolidayFee !== null && affiliate.paymentHolidayFee !== undefined ? formatMoney(affiliate.paymentHolidayFee) : "Not set"}
                                    color={affiliate.paymentHolidayFee !== null && affiliate.paymentHolidayFee !== undefined ? "success" : "default"}
                                    variant="outlined"
                                    sx={{ fontWeight: 'bold' }}
                                />
                            </Box>
                            
                            <Divider sx={{ my: 1.5 }} />
                            
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>Minimum Holiday Period:</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Minimum duration for payment holiday requests
                                    </Typography>
                                </Box>
                                <Chip
                                    label={`${affiliate.minimumPaymentHolidayWeeks || 4} weeks (${(affiliate.minimumPaymentHolidayWeeks || 4) * 7} days)`}
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontWeight: 'bold' }}
                                />
                            </Box>
                        </Paper>
                        
                        {(affiliate.paymentHolidayFee === null || affiliate.paymentHolidayFee === undefined) && (
                            <Paper 
                                elevation={0} 
                                sx={{ 
                                    mt: 2, 
                                    p: 2, 
                                    bgcolor: 'warning.lighter', 
                                    borderLeft: 4, 
                                    borderColor: 'warning.main',
                                    borderRadius: 1 
                                }}
                            >
                                <Typography variant="body2" color="warning.dark">
                                    ⚠️ Payment holiday fee is not configured. Users cannot request payment holidays.
                                </Typography>
                            </Paper>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            {/* Stebby API Modal */}
            <StebbyApiModal
                open={stebbyModalOpen}
                onClose={() => setStebbyModalOpen(false)}
                affiliateId={affiliate.id}
                affiliateName={affiliate.name}
            />

            {/* Montonio API Modal */}
            <MontonioApiModal
                open={montonioModalOpen}
                onClose={() => setMontonioModalOpen(false)}
                affiliateId={affiliate.id}
                affiliateName={affiliate.name}
            />
        </Grid>
    );
}